import React, { useState } from 'react';
import { socialWallApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import LikeButton from './LikeButton';
import CommentSection from './CommentSection';
import ImageGallery from './ImageGallery';
import ImageLightbox from './ImageLightbox';
import BirthdayGreeting from './BirthdayGreeting';
import { MoreVertical, Edit, Trash2, X, Bell } from 'react-feather';
import { Button } from '../ui/button';
import './SocialWall.css';

const PostCard = ({
  post,
  currentUserId,
  onPostUpdated,
  onPostDeleted,
  onLikeToggle,
  onCommentAdded,
  onCommentUpdated,
  onCommentDeleted
}) => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content);
  const [showMenu, setShowMenu] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  const isOwner = post.user_id === currentUserId;
  const images = post.attachments?.filter(att => att.file_type === 'image') || [];
  const videos = post.attachments?.filter(att => att.file_type === 'video') || [];

  const handleEdit = async () => {
    try {
      const updatedPost = await socialWallApi.update(post.id, { content: editContent });
      onPostUpdated(updatedPost);
      setIsEditing(false);
      setShowMenu(false);
    } catch (err) {
      console.error('Error updating post:', err);
      alert('Failed to update post');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }
    
    setIsDeleting(true);
    try {
      await socialWallApi.delete(post.id);
      onPostDeleted(post.id);
    } catch (err) {
      console.error('Error deleting post:', err);
      alert('Failed to delete post');
      setIsDeleting(false);
    }
  };

  const handleImageClick = (index) => {
    setLightboxImageIndex(index);
    setShowLightbox(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (isDeleting) {
    return null;
  }

  // Extract birthday person's name from post content
  const extractBirthdayName = () => {
    if (post.post_type !== 'birthday') return null;
    
    const content = post.content || '';
    
    // Try to extract name from common birthday patterns
    const patterns = [
      /(?:happy\s+birthday|birthday|happy\s+bday|hbd)[\s,]+([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)?)/i,
      /([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)?)['']?s\s+birthday/i,
      /celebrating\s+([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)?)/i,
      /wishing\s+([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)?)/i,
      /^([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)?)[\s!.,]/i, // Name at start of content
    ];
    
    for (const pattern of patterns) {
      const match = content.match(pattern);
      if (match && match[1]) {
        const name = match[1].trim();
        // Filter out common words that might be mistaken for names
        const commonWords = ['happy', 'birthday', 'wishing', 'celebrating', 'team', 'member'];
        if (!commonWords.includes(name.toLowerCase())) {
          return name;
        }
      }
    }
    
    // If no name found in content, use the post author's name
    return post.user?.display_name || null;
  };

  const birthdayName = extractBirthdayName();

  return (
    <div className={`post-card ${post.post_type === 'birthday' ? 'birthday-post' : ''} ${post.post_type === 'announcement' ? 'announcement-post' : ''}`}>
      {post.post_type === 'birthday' && (
        <BirthdayGreeting name={birthdayName} />
      )}
      
      {post.post_type === 'announcement' && (
        <div className="announcement-badge">
          <Bell size={16} />
          <span>Announcement</span>
        </div>
      )}
      
      <div className="post-header">
        <div className="post-author">
          <div className="post-author-avatar">
            {post.user?.photo_url ? (
              <img src={post.user.photo_url} alt={post.user.display_name} />
            ) : (
              <div className="post-author-initials">
                {post.user?.display_name?.charAt(0) || 'U'}
              </div>
            )}
          </div>
          <div className="post-author-info">
            <div className="post-author-name">{post.user?.display_name || 'Unknown User'}</div>
            <div className="post-date">{formatDate(post.created_at)}</div>
          </div>
        </div>
        
        {isOwner && (
          <div className="post-menu">
            <button
              className="post-menu-button"
              onClick={() => setShowMenu(!showMenu)}
            >
              <MoreVertical size={18} />
            </button>
            {showMenu && (
              <div className="post-menu-dropdown">
                <button
                  className="post-menu-item"
                  onClick={() => {
                    setIsEditing(true);
                    setShowMenu(false);
                  }}
                >
                  <Edit size={16} />
                  Edit
                </button>
                <button
                  className="post-menu-item delete"
                  onClick={handleDelete}
                >
                  <Trash2 size={16} />
                  Delete
                </button>
                <button
                  className="post-menu-item"
                  onClick={() => setShowMenu(false)}
                >
                  <X size={16} />
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="post-content">
        {isEditing ? (
          <div className="post-edit">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="post-edit-textarea"
              rows={4}
            />
            <div className="post-edit-actions">
              <Button
                variant="primary"
                size="small"
                onClick={handleEdit}
              >
                Save
              </Button>
              <Button
                variant="secondary"
                size="small"
                onClick={() => {
                  setIsEditing(false);
                  setEditContent(post.content);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="post-text">{post.content}</div>
        )}

        {images.length > 0 && (
          <ImageGallery
            images={images}
            onImageClick={handleImageClick}
          />
        )}

        {videos.length > 0 && (
          <div className="post-videos">
            {videos.map((video, index) => (
              <video
                key={index}
                src={video.url}
                controls
                className="post-video"
              >
                Your browser does not support the video tag.
              </video>
            ))}
          </div>
        )}
      </div>

      <div className="post-actions">
        <LikeButton
          postId={post.id}
          isLiked={post.is_liked}
          likesCount={post.likes_count || 0}
          onToggle={onLikeToggle}
        />
      </div>

      <CommentSection
        postId={post.id}
        comments={post.comments || []}
        onCommentAdded={onCommentAdded}
        onCommentUpdated={onCommentUpdated}
        onCommentDeleted={onCommentDeleted}
      />

      {showLightbox && (
        <ImageLightbox
          images={images}
          currentIndex={lightboxImageIndex}
          onClose={() => setShowLightbox(false)}
          onNext={() => setLightboxImageIndex((prev) => (prev + 1) % images.length)}
          onPrev={() => setLightboxImageIndex((prev) => (prev - 1 + images.length) % images.length)}
        />
      )}
    </div>
  );
};

export default PostCard;

